// src/pages/vendor/FarmerVendorSignup.js
import { useMemo, useState } from "react";
import { auth, db } from "../../firebaseConfig";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";

/* =========================
   MASTER DATA (Canonical Definitions)
========================= */

// 🔹 Canonical component IDs for business categories
const COMPONENT_IDS = {
  MACH_BANK: "MACH_BANK",
  IRRIGATION: "IRRIGATION",
  INM_IPM: "INM_IPM",
  GAP_SOIL: "GAP_SOIL",
  PRECISION: "PRECISION",
  INNOVATIVE: "INNOVATIVE",
};

// 🔹 Legacy to Canonical mapping for backward compatibility and data normalization
const CANONICAL_CATEGORY_MAP = {
  MACH_BANK: "MACH_BANK",
  MACHINERY: "MACH_BANK",
  IRRIGATION: "IRRIGATION",
  INM_IPM: "INM_IPM",
  GAP_SOIL: "GAP_SOIL",
  PRECISION: "PRECISION",
  INNOVATIVE: "INNOVATIVE",
  TOOLS: "INNOVATIVE",
};

// 🔹 Utility to normalize category IDs
const normalizeCategoryId = (raw) => CANONICAL_CATEGORY_MAP[raw] || raw || "";

// 🔹 UI-friendly category list (using canonical IDs)
const CATEGORIES = [
  { id: COMPONENT_IDS.MACH_BANK, label: "Machinery Bank / Tractor", desc: "Tractors • Implements • Machinery services" },
  { id: COMPONENT_IDS.IRRIGATION, label: "Drip / Sprinkler / Talavdi", desc: "Micro irrigation • fittings • installation" },
  { id: COMPONENT_IDS.INM_IPM, label: "Fertilizer / INM / IPM", desc: "Nutrients • bio inputs • crop protection" },
  { id: COMPONENT_IDS.GAP_SOIL, label: "Soil Testing Lab / GAP", desc: "Soil health • advisory • GAP compliance" },
  { id: COMPONENT_IDS.PRECISION, label: "Precision Tech (Drone / IoT)", desc: "Drone spray • sensors • smart farming" },
  { id: COMPONENT_IDS.INNOVATIVE, label: "Innovative Tools", desc: "New age tools • custom solutions" },
];

// 🔹 Location master data (restricted to Gujarat with updated districts/talukas)
const GUJARAT_DISTRICTS = [
  {
    id: "Banaskantha",
    label: "Banaskantha",
    talukas: [
      "Palanpur",
      "Vadgam",
      "Danta",
      "Amirgadh",
      "Deesa",
      "Kankrej",
      "Dantiwada",
      "Dhanera",
      "Hadad",
      "Ogad",
    ],
  },
  {
    id: "Vav-Tharad",
    label: "Vav-Tharad",
    talukas: [
      "Vav",
      "Tharad",
      "Suigam",
      "Lakhani",
      "Diyodar",
      "Bhabhar",
      "Dharnidhar (ઢીમા)",
      "Rah",
    ],
  },
  {
    id: "Kutch",
    label: "Kutch",
    talukas: [
      "Bhuj",
      "Gandhidham",
      "Anjar",
      "Mandvi",
      "Mundra",
      "Rapar",
      "Nakhatrana",
      "Abdasa",
      "Lakhpat",
    ],
  },
];

// 🔹 Indian tractor brands for machinery vendors
const TRACTOR_BRANDS = [
  "John Deere",
  "Mahindra",
  "Swaraj",
  "Sonalika",
  "New Holland",
  "TAFE / Massey Ferguson",
  "Eicher",
  "Kubota",
  "Powertrac",
  "Farmtrac",
];

/* =========================
   ✅ MACHINERY MASTER (Group → Items)
========================= */

const toId = (label) =>
  String(label || "")
    .toUpperCase()
    .replace(/&/g, "AND")
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const TRACTOR_SUBSIDY_ITEMS = [
  { id: "TRACTOR_2WD_UPTO_20PTO", label: "Tractor 2WD (up to 20 PTO HP)" },
  { id: "TRACTOR_4WD_UPTO_20PTO", label: "Tractor 4WD (up to 20 PTO HP)" },
  { id: "TRACTOR_2WD_20_40PTO", label: "Tractor 2WD (above 20 PTO HP and up to 40 PTO HP)" },
  { id: "TRACTOR_4WD_20_40PTO", label: "Tractor 4WD (above 20 PTO HP and up to 40 PTO HP)" },
  { id: "TRACTOR_2WD_40_50PTO", label: "Tractor 2WD (above 40 PTO HP and up to 50 PTO HP)" },
  { id: "TRACTOR_4WD_40_50PTO", label: "Tractor 4WD (above 40 PTO HP and up to 50 PTO HP)" },
  { id: "TRACTOR_2WD_ABOVE_50PTO", label: "Tractor 2WD (Above 50 PTO HP)" },
  { id: "TRACTOR_4WD_ABOVE_50PTO", label: "Tractor 4WD (Above 50 PTO HP)" },
];

const MACH_GROUPS = [
  { id: "TRACTORS", label: "Tractors" },
  { id: "POWER_TILLERS", label: "Power Tillers" },
  { id: "COMBINE_HARVESTERS", label: "Combine Harvesters" },
  { id: "KISAN_DRONE", label: "Kisan Drone / Spraying Drone" },
  { id: "RICE_TRANSPLANTERS", label: "Rice / Paddy Transplanters" },
  { id: "SELF_PROPELLED_AGRI", label: "Self-propelled Agri / Hort Machinery" },
  { id: "LAND_DEV_TILLAGE", label: "Land Development & Tillage Implements" },
  { id: "SOWING_PLANTING", label: "Sowing / Planting / Seeding" },
  { id: "INTER_CULTIVATION", label: "Inter-cultivation Equipment" },
  { id: "HARVESTING_THRESHING", label: "Harvesting & Threshing" },
  { id: "RESIDUE_MANAGEMENT", label: "Residue / Hay / Forage Equipment" },
  { id: "POST_HARVEST_VALUE", label: "Post-harvest & Value Addition" },
  { id: "PLANT_PROTECTION", label: "Plant Protection (Sprayers etc.)" },
  { id: "IRRIGATION_EQUIP", label: "Irrigation Equipments / Pumps" },
  { id: "SPECIALIZED_AGRI", label: "Specialized Agricultural Machinery" },
  { id: "MANUAL_ANIMAL", label: "Manual / Animal-drawn Implements" },
  { id: "OTHERS", label: "Other Farm Machinery" },
];

const MACHINERY_MASTER = {
  TRACTORS: { label: "Tractors", items: TRACTOR_SUBSIDY_ITEMS.map((x) => ({ id: x.id, label: x.label })) },
  POWER_TILLERS: {
    label: "Power Tillers",
    items: ["Power Tiller (8 BHP and up to 11 BHP)", "Power Tiller (Above 11 BHP)"].map((label) => ({
      id: toId(label),
      label,
    })),
  },
  COMBINE_HARVESTERS: {
    label: "Combine Harvesters",
    items: [
      "Combine Harvester (Self-propelled)",
      "Combine Harvester - Tractor powered (without tractor)",
      "Combine Harvester - Tractor powered (with tractor)",
      "Combine Harvester - Chopper",
      "Combine Harvester (Track Type) - < 6 feet cutter bar width",
      "Combine Harvester (Track Type) - > 6 feet cutter bar width",
      "Combine Harvester for potato/groundnut/other tuber",
      "Sugarcane Harvester (Self-propelled) - Wheel/Track type",
      "Maize Combine Harvester (Self-propelled)",
    ].map((label) => ({ id: toId(label), label })),
  },
  KISAN_DRONE: {
    label: "Kisan Drone / Spraying Drone",
    items: [
      "Kisan Drone (Agriculture Spraying Drone)",
      "Drone Battery & Charger Set",
      "Drone Spray Pump / Nozzle Kit",
      "Drone Training / Demonstration Setup",
    ].map((label) => ({ id: toId(label), label })),
  },
  RICE_TRANSPLANTERS: {
    label: "Rice / Paddy Transplanters",
    items: [
      "Self-Propelled Rice Transplanter",
      "Self-Propelled Rice Transplanter - 4 rows",
      "Self-Propelled Rice Transplanter - above 8 rows",
    ].map((label) => ({ id: toId(label), label })),
  },
  SELF_PROPELLED_AGRI: {
    label: "Self-propelled Agri / Hort Machinery",
    items: [
      "Crop Reaper cum Binder",
      "Power Weeder (Engine Operated)",
      "Post Hole Digger / Auger (Self-propelled)",
      "Pneumatic / Other Planter",
      "Tea Leaf Harvester (Hand Held)",
      "Self-Propelled Tool Bar (Ride-on)",
      "Self-Propelled Electric Agricultural Tool Bar",
      "Track Trolley",
      "Nursery Media Filling Machine",
      "Brush Cutter (Electric / Engine Powered)",
      "Electric Tiller / Weeder",
    ].map((label) => ({ id: toId(label), label })),
  },
  LAND_DEV_TILLAGE: {
    label: "Land Development & Tillage Implements",
    items: [
      "MB Plough (Single Bottom)",
      "MB Plough (Single Bottom - Reversible)",
      "Disc Plough",
      "Cultivator",
      "Harrow",
      "Leveler Blade",
      "Cage Wheel",
      "Furrow Opener",
      "Ridger",
      "Weed Slasher",
      "Bund Former",
      "Crust Breaker",
      "Rotopuddler",
      "Rotocultivator",
      "Power Harrow",
      "Rotavator (Up to 4 ft.)",
      "Rotavator (Up to 5 ft.)",
      "Chisel Plough",
      "Laser Land Leveller",
    ].map((label) => ({ id: toId(label), label })),
  },
  SOWING_PLANTING: {
    label: "Sowing / Planting / Seeding",
    items: [
      "Potato Planter",
      "Potato Digger",
      "Groundnut Digger",
      "Strip Till Drill",
      "Onion Harvester",
      "Raised Bed Planter",
      "Sugarcane Cutter / Stripper",
      "Multi Crop Planter",
      "Pneumatic Planter",
      "Plastic Mulch Laying Machine",
      "Seed Treating Drum",
      "Seed Cum Fertilizer Drill",
      "Aqua Ferti Seed Drill",
      "Direct Rice Seeder (DSR)",
      "Zero-Till Drill",
    ].map((label) => ({ id: toId(label), label })),
  },
  INTER_CULTIVATION: {
    label: "Inter-cultivation Equipment",
    items: ["Grass Weed Slasher", "Inter Row cum Intra Row Weeder", "Power Weeder (Engine Operated)"].map(
      (label) => ({ id: toId(label), label })
    ),
  },
  HARVESTING_THRESHING: {
    label: "Harvesting & Threshing",
    items: ["Ground Nut Pod Stripper", "Thresher", "Multi Crop Thresher", "Paddy Thresher", "Winnowing Fan", "Maize Sheller", "Mower", "Flail Harvester", "Chaff Cutter"].map(
      (label) => ({ id: toId(label), label })
    ),
  },
  RESIDUE_MANAGEMENT: {
    label: "Residue / Hay / Forage Equipment",
    items: [
      "Sugarcane Trash Cutter",
      "Coconut Frond Chopper",
      "Stubble Shaver",
      "Straw Reaper",
      "Baler (Small Capacity)",
      "Baler (Round - up to 16 kg)",
      "Baler (Round - above 16 and up to 25 kg per bale)",
      "Feed Block Making Machine",
      "Straw Chopper / Shredder / Mulcher (Mounted type)",
    ].map((label) => ({ id: toId(label), label })),
  },
  POST_HARVEST_VALUE: {
    label: "Post-harvest & Value Addition",
    items: ["Grain Cleaner", "Grain Grader", "Seed Cleaner & Grader", "Mini Dal Mill", "Mini Rice Mill", "Flour Mill", "Oil Expeller", "Packaging / Sealing Machine"].map(
      (label) => ({ id: toId(label), label })
    ),
  },
  PLANT_PROTECTION: {
    label: "Plant Protection (Sprayers etc.)",
    items: ["Manual Knapsack Sprayer", "Battery Operated Sprayer", "Engine Operated Sprayer", "Boom Sprayer", "Orchard Sprayer", "Drone Sprayer"].map(
      (label) => ({ id: toId(label), label })
    ),
  },
  IRRIGATION_EQUIP: {
    label: "Irrigation Equipments / Pumps",
    items: ["Centrifugal Pump", "Submersible Pump", "Solar Pump", "Diesel Pump", "Electric Pump", "Pipe Laying / Carrying System"].map(
      (label) => ({ id: toId(label), label })
    ),
  },
  SPECIALIZED_AGRI: {
    label: "Specialized Agricultural Machinery",
    items: ["Horticulture Pruning Machine", "Orchard Platform / Lift", "Cotton Picker", "Sugarcane Bud Chipper", "Banana Fibre Extractor"].map(
      (label) => ({ id: toId(label), label })
    ),
  },
  MANUAL_ANIMAL: {
    label: "Manual / Animal-drawn Implements",
    items: ["Country Plough", "Blade Harrow", "Spike Tooth Harrow", "Manual Weeder", "Wheel Hoe"].map((label) => ({
      id: toId(label),
      label,
    })),
  },
  OTHERS: {
    label: "Other Farm Machinery",
    items: ["Trailer / Trolley (Up to 3 Ton)", "Trailer / Trolley (Up to 5 Ton)", "Narrow Rear Rim (One Pair)", "Tool Bar", "Track Trolley"].map(
      (label) => ({ id: toId(label), label })
    ),
  },
};

/* =========================
   UTILITY FUNCTIONS
========================= */

const cx = (...classes) => classes.filter(Boolean).join(" ");

/* =========================
   MAIN COMPONENT
========================= */

export default function FarmerVendorSignup() {
  const [step, setStep] = useState(1); // 1 - Company/Contact, 2 - Location, 3 - Categories

  const [form, setForm] = useState({
    companyName: "",
    contactName: "",
    mobile: "",
    email: "",
    password: "",
    confirmPassword: "",
    gstin: "",
    pan: "",
    address: "",
    state: "Gujarat",
    district: "",

    // ✅ CHANGED: single taluka → multiple talukas
    serviceTalukas: [], // ["Deesa","Dantiwada"]

    pincode: "",
    categories: [],
    tractorBrands: [],

    // ✅ Machinery
    machineryGroups: [],
    machineryItems: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedDistrict = useMemo(
    () => GUJARAT_DISTRICTS.find((d) => d.id === form.district),
    [form.district]
  );
  const talukaOptions = useMemo(() => selectedDistrict?.talukas || [], [selectedDistrict]);

  const isMachineryVendor = useMemo(
    () => form.categories.includes(COMPONENT_IDS.MACH_BANK),
    [form.categories]
  );

  const progress = useMemo(() => (step === 1 ? 34 : step === 2 ? 67 : 100), [step]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError("");

    if (name === "district") {
      // ✅ district change resets taluka selections
      setForm((prev) => ({ ...prev, district: value, serviceTalukas: [] }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ NEW: Multi-taluka toggle
  const toggleTaluka = (taluka) => {
    setError("");
    setForm((prev) => {
      const exists = prev.serviceTalukas.includes(taluka);
      const next = exists
        ? prev.serviceTalukas.filter((t) => t !== taluka)
        : [...prev.serviceTalukas, taluka];
      return { ...prev, serviceTalukas: next };
    });
  };

  const selectAllTalukas = () => {
    if (!selectedDistrict) return;
    setForm((prev) => ({ ...prev, serviceTalukas: [...talukaOptions] }));
  };

  const clearTalukas = () => {
    setForm((prev) => ({ ...prev, serviceTalukas: [] }));
  };

  const toggleCategory = (id) => {
    setError("");
    setForm((prev) => {
      const next = prev.categories.includes(id)
        ? prev.categories.filter((x) => x !== id)
        : [...prev.categories, id];

      // ✅ If MACH_BANK removed, also clear machinery extras
      if (!next.includes(COMPONENT_IDS.MACH_BANK)) {
        return { ...prev, categories: next, tractorBrands: [], machineryGroups: [], machineryItems: [] };
      }
      return { ...prev, categories: next };
    });
  };

  const toggleTractorBrand = (brand) => {
    setError("");
    setForm((prev) => ({
      ...prev,
      tractorBrands: prev.tractorBrands.includes(brand)
        ? prev.tractorBrands.filter((b) => b !== brand)
        : [...prev.tractorBrands, brand],
    }));
  };

  const toggleMachGroup = (groupId) => {
    setError("");
    setForm((prev) => {
      const exists = prev.machineryGroups.includes(groupId);
      const nextGroups = exists
        ? prev.machineryGroups.filter((g) => g !== groupId)
        : [...prev.machineryGroups, groupId];

      const nextItems = exists
        ? prev.machineryItems.filter((it) => it.groupId !== groupId)
        : prev.machineryItems;

      return { ...prev, machineryGroups: nextGroups, machineryItems: nextItems };
    });
  };

  const toggleMachItem = (groupId, item) => {
    setError("");
    setForm((prev) => {
      const key = `${groupId}__${item.id}`;
      const exists = prev.machineryItems.some((x) => `${x.groupId}__${x.itemId}` === key);

      const next = exists
        ? prev.machineryItems.filter((x) => `${x.groupId}__${x.itemId}` !== key)
        : [...prev.machineryItems, { groupId, itemId: item.id, label: item.label }];

      return { ...prev, machineryItems: next };
    });
  };

  const validateStep1 = () => {
    if (!form.companyName.trim()) return "Company Name is required.";
    if (!form.contactName.trim()) return "Contact Person is required.";
    if (!form.mobile.trim() || !/^\d{10}$/.test(form.mobile.trim()))
      return "Valid 10-digit Mobile is required.";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email.trim()))
      return "Valid Email is required.";
    if (form.password.length < 6) return "Password must be at least 6 characters.";
    if (form.password !== form.confirmPassword) return "Passwords do not match.";
    return "";
  };

  const validateStep2 = () => {
    if (!form.district) return "District is required.";
    if (!form.serviceTalukas.length) return "Select at least one Taluka (you can choose multiple).";
    if (form.pincode && !/^\d{6}$/.test(form.pincode.trim()))
      return "Valid 6-digit Pincode is required.";
    return "";
  };

  const validateStep3 = () => {
    if (!form.categories.length) return "At least one business category is required.";
    return "";
  };

  const handleNext = () => {
    let validationError = "";
    if (step === 1) validationError = validateStep1();
    else if (step === 2) validationError = validateStep2();

    if (validationError) {
      setError(validationError);
      return;
    }
    setStep((prev) => Math.min(3, prev + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    setError("");
    setStep((prev) => Math.max(1, prev - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const errors = [validateStep1(), validateStep2(), validateStep3()].filter(Boolean);
    if (errors.length) {
      setError(errors[0]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { user } = await createUserWithEmailAndPassword(
        auth,
        form.email.trim().toLowerCase(),
        form.password
      );
      const uid = user.uid;

      const canonicalCategories = Array.from(new Set(form.categories.map(normalizeCategoryId)));

      // ✅ backward compatible taluka field (first selected)
      const primaryTaluka = (form.serviceTalukas?.[0] || "").trim();

      await setDoc(doc(db, "farmer_vendors", uid), {
        uid,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        status: "pending",

        company_name: form.companyName.trim(),
        contact_name: form.contactName.trim(),
        mobile: form.mobile.trim(),
        email: form.email.trim().toLowerCase(),
        gstin: form.gstin.trim().toUpperCase(),
        pan: form.pan.trim().toUpperCase(),
        address: form.address.trim(),
        state: form.state.trim(),
        district: form.district.trim(),

        // ✅ NEW main field
        serviceTalukas: (form.serviceTalukas || []).map((t) => String(t).trim()).filter(Boolean),

        // ✅ OLD field kept so old pages don't break
        taluka: primaryTaluka,

        pincode: form.pincode.trim(),

        componentsOffered: canonicalCategories,
        categories: canonicalCategories,

        tractorBrands: form.tractorBrands || [],
        machineryGroups: form.machineryGroups || [],
        machineryItems: form.machineryItems || [],
      });

      alert(
        "Vendor registration submitted successfully.\nYour account is pending approval by the Society. You can now login with your email & password."
      );
      window.location.href = "/vendor/login";
    } catch (err) {
      console.error("Vendor signup error:", err);
      setError(`Error: ${err.message || "Failed to create account. Please try again."}`);
    } finally {
      setLoading(false);
    }
  };

  const stepTitle =
    step === 1
      ? "Company & Contact Details"
      : step === 2
      ? "Address & Location"
      : "Business Categories";

  return (
    <div className="min-h-screen bg-slate-950">
      {/* ====== PREMIUM HERO / HEADER ====== */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.22),transparent_40%),radial-gradient(circle_at_80%_30%,rgba(59,130,246,0.18),transparent_45%),radial-gradient(circle_at_50%_90%,rgba(244,63,94,0.10),transparent_45%)]" />
        <div className="relative border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] tracking-[0.22em] uppercase text-white/70">
                  <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_18px_rgba(16,185,129,0.6)]" />
                  Shine Agri Allied &amp; Marketing
                </div>
                <h1 className="mt-3 text-2xl md:text-3xl font-black tracking-tight text-white">
                  Vendor Registration Portal
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-white/70">
                  Government-style onboarding • One-time verification • Account activation after Society approval
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <TopBadge label="Mode" value="Online" />
                <TopBadge label="Fee" value="₹0 (Free)" tone="emerald" />
                <TopBadge label="Status" value="Pending Approval" tone="amber" />
              </div>
            </div>

            <div className="mt-6">
              <div className="flex items-center justify-between text-[11px] text-white/70">
                <span>Step {step} of 3</span>
                <span>{progress}% Completed</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden border border-white/10">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 via-emerald-300 to-cyan-300 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ====== BODY ====== */}
      <div className="bg-slate-100 min-h-[calc(100vh-180px)]">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* LEFT PANE */}
            <aside className="lg:col-span-4">
              <div className="sticky top-6 space-y-4">
                <Card>
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 h-9 w-9 rounded-2xl bg-emerald-600 text-white grid place-items-center shadow">
                      <ShieldIcon />
                    </div>
                    <div>
                      <div className="text-sm font-extrabold text-slate-900">Verification & Trust</div>
                      <p className="mt-1 text-[12px] text-slate-600 leading-relaxed">
                        Registration is <b>free</b>. Your account stays <b>inactive</b> until Admin approval.
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <TinyStat label="Approval" value="Required" />
                    <TinyStat label="Region" value="Gujarat" />
                    <TinyStat label="Steps" value="3 Simple" />
                    <TinyStat label="Status" value="Pending" />
                  </div>
                </Card>

                <Card className="bg-gradient-to-br from-white to-slate-50">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                    Registration Guide
                  </div>

                  <div className="mt-3 space-y-3">
                    <GuideRow active={step === 1} n="1" title="Company Details" desc="Name, contact, login credentials" />
                    <GuideRow active={step === 2} n="2" title="Location" desc="District & Taluka for service mapping" />
                    <GuideRow active={step === 3} n="3" title="Categories" desc="Choose services you provide" />
                  </div>

                  <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-3">
                    <div className="text-[12px] font-bold text-slate-900">Note</div>
                    <div className="text-[12px] text-slate-600 mt-1">
                      Enabled for Gujarat. Taluka now supports <b>multiple selection</b>.
                    </div>
                  </div>
                </Card>
              </div>
            </aside>

            {/* RIGHT FORM */}
            <section className="lg:col-span-8">
              {error && (
                <div className="mb-4 rounded-3xl border border-red-200 bg-red-50 p-4 shadow-sm">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-2xl bg-red-600 text-white grid place-items-center shadow">
                      <AlertIcon />
                    </div>
                    <div>
                      <div className="text-sm font-extrabold text-red-800">Please fix this</div>
                      <div className="text-[12px] text-red-700 mt-1">{error}</div>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="rounded-[28px] border border-slate-200 bg-white shadow-xl overflow-hidden">
                {/* Header */}
                <div className="border-b border-slate-200 bg-white">
                  <div className="p-5 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                          Vendor Registration
                        </div>
                        <div className="mt-1 text-xl md:text-2xl font-black text-slate-900 tracking-tight">
                          {stepTitle}
                        </div>
                        <div className="text-[12px] text-slate-600 mt-1">
                          Fill accurate details. Automated validation ensures clean data.
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <StepChip active={step === 1} label="Company" />
                        <StepChip active={step === 2} label="Location" />
                        <StepChip active={step === 3} label="Categories" />
                      </div>
                    </div>
                  </div>

                  <div className="px-5 md:px-6 pb-5">
                    <StepperLine step={step} />
                  </div>
                </div>

                {/* BODY */}
                <div className="p-5 md:p-6">
                  {step === 1 && (
                    <div className="space-y-5">
                      <SectionHeader
                        icon={<OfficeIcon />}
                        title="Company & Contact Details"
                        subtitle="Provide official details for verification and login authentication."
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Company Name *" name="companyName" value={form.companyName} onChange={handleChange} />
                        <Field label="Contact Person *" name="contactName" value={form.contactName} onChange={handleChange} />

                        <Field label="Mobile *" name="mobile" value={form.mobile} onChange={handleChange} maxLength={10} inputMode="numeric" />
                        <Field label="Email (Login ID) *" name="email" type="email" value={form.email} onChange={handleChange} />

                        <Field label="Password *" name="password" type="password" value={form.password} onChange={handleChange} />
                        <Field label="Confirm Password *" name="confirmPassword" type="password" value={form.confirmPassword} onChange={handleChange} />

                        <Field label="GSTIN (Optional)" name="gstin" value={form.gstin} onChange={handleChange} maxLength={15} />
                        <Field label="PAN (Optional)" name="pan" value={form.pan} onChange={handleChange} maxLength={10} />
                      </div>

                      <HintRow text="Your Email will be used as Login ID. Password minimum 6 characters." />
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-5">
                      <SectionHeader
                        icon={<PinIcon />}
                        title="Address & Location Details"
                        subtitle="Location helps taluka-wise farmer discovery and service mapping."
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <Field label="Full Address (Optional)" name="address" value={form.address} onChange={handleChange} textarea rows={4} />
                        </div>

                        <div>
                          <Label>State</Label>
                          <select name="state" value={form.state} onChange={handleChange} className={selectClass()} disabled>
                            <option value="Gujarat">Gujarat</option>
                          </select>
                        </div>

                        <div>
                          <Label>District *</Label>
                          <select name="district" value={form.district} onChange={handleChange} className={selectClass()}>
                            <option value="">Select District</option>
                            {GUJARAT_DISTRICTS.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* ✅ CHANGED: Multi Taluka Selection */}
                        <div className="md:col-span-2">
                          <div className="flex items-center justify-between">
                            <Label>Taluka(s) * (You can select multiple)</Label>
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={selectAllTalukas}
                                disabled={!selectedDistrict}
                                className={cx(
                                  "text-[11px] font-semibold px-3 py-1.5 rounded-full border transition",
                                  !selectedDistrict
                                    ? "border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed"
                                    : "border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
                                )}
                              >
                                Select All
                              </button>
                              <button
                                type="button"
                                onClick={clearTalukas}
                                disabled={!form.serviceTalukas.length}
                                className={cx(
                                  "text-[11px] font-semibold px-3 py-1.5 rounded-full border transition",
                                  !form.serviceTalukas.length
                                    ? "border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed"
                                    : "border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
                                )}
                              >
                                Clear
                              </button>
                            </div>
                          </div>

                          <div
                            className={cx(
                              "mt-2 rounded-3xl border border-slate-200 bg-slate-50 p-3",
                              !selectedDistrict ? "opacity-60" : ""
                            )}
                          >
                            {!selectedDistrict ? (
                              <div className="text-[12px] text-slate-600">
                                Select District first to choose Taluka(s).
                              </div>
                            ) : (
                              <div className="flex flex-wrap gap-2">
                                {talukaOptions.map((t) => {
                                  const active = form.serviceTalukas.includes(t);
                                  return (
                                    <button
                                      type="button"
                                      key={t}
                                      onClick={() => toggleTaluka(t)}
                                      className={cx(
                                        "px-3 py-2 rounded-full border text-[12px] font-semibold transition",
                                        active
                                          ? "border-emerald-300 bg-emerald-50 text-emerald-900 shadow"
                                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                                      )}
                                    >
                                      {active ? "✓ " : ""}{t}
                                    </button>
                                  );
                                })}
                              </div>
                            )}

                            <div className="mt-3 text-[11px] text-slate-600">
                              Selected: <b>{form.serviceTalukas.length}</b>
                              {form.serviceTalukas.length ? (
                                <span className="ml-2">
                                  ({form.serviceTalukas.join(", ")})
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        <Field label="Pincode (Optional)" name="pincode" value={form.pincode} onChange={handleChange} maxLength={6} inputMode="numeric" />
                      </div>

                      <HintRow text="Tip: District change will reset Taluka selections automatically (to avoid wrong mapping)." />
                    </div>
                  )}

                  {/* STEP 3 (same as your file) */}
                  {step === 3 && (
                    <div className="space-y-5">
                      <SectionHeader
                        icon={<GridIcon />}
                        title="Business Categories"
                        subtitle="Select services you provide (minimum one required for approval)."
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {CATEGORIES.map((c) => {
                          const active = form.categories.includes(c.id);
                          return (
                            <button
                              type="button"
                              key={c.id}
                              onClick={() => toggleCategory(c.id)}
                              className={cx(
                                "text-left group rounded-3xl border p-4 transition-all duration-200",
                                "focus:outline-none focus:ring-2 focus:ring-slate-900",
                                active
                                  ? "border-emerald-300 bg-emerald-50 shadow-md"
                                  : "border-slate-200 bg-white hover:bg-slate-50 hover:shadow"
                              )}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-3">
                                  <span
                                    className={cx(
                                      "mt-0.5 h-10 w-10 rounded-2xl grid place-items-center border",
                                      active
                                        ? "bg-white border-emerald-200 text-emerald-700"
                                        : "bg-slate-50 border-slate-200 text-slate-700"
                                    )}
                                  >
                                    {active ? <CheckIcon /> : <PlusIcon />}
                                  </span>

                                  <div>
                                    <div className="text-sm font-extrabold text-slate-900">{c.label}</div>
                                    <div className="text-[12px] text-slate-600 mt-1 leading-snug">{c.desc}</div>
                                    <div className="mt-2 text-[11px] text-slate-500">
                                      Component ID: <span className="font-semibold">{c.id}</span>
                                    </div>
                                  </div>
                                </div>

                                <span
                                  className={cx(
                                    "shrink-0 text-[11px] font-semibold px-3 py-1 rounded-full border transition",
                                    active
                                      ? "border-emerald-300 bg-white text-emerald-800"
                                      : "border-slate-200 bg-slate-50 text-slate-600 group-hover:bg-white"
                                  )}
                                >
                                  {active ? "Selected" : "Select"}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      {/* Machinery block (your existing) */}
                      {isMachineryVendor && (
                        <div className="space-y-4">
                          {/* Tractor Brands */}
                          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                              <div>
                                <div className="text-sm font-black text-slate-900">Tractor Brands (Optional)</div>
                                <div className="text-[12px] text-slate-600 mt-1">
                                  Helps farmers filter vendors by brand for faster discovery.
                                </div>
                              </div>
                              <span className="text-[11px] font-semibold px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-700">
                                Selected: {form.tractorBrands.length}
                              </span>
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2">
                              {TRACTOR_BRANDS.map((brand) => {
                                const active = form.tractorBrands.includes(brand);
                                return (
                                  <button
                                    type="button"
                                    key={brand}
                                    onClick={() => toggleTractorBrand(brand)}
                                    className={cx(
                                      "px-3 py-2 rounded-full border text-[12px] font-semibold transition-all duration-200",
                                      "focus:outline-none focus:ring-2 focus:ring-slate-900",
                                      active
                                        ? "border-slate-700 bg-white text-slate-900 shadow"
                                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100 hover:shadow"
                                    )}
                                  >
                                    <span className="inline-flex items-center gap-2">
                                      <span className={cx("h-2 w-2 rounded-full", active ? "bg-emerald-500" : "bg-slate-300")} />
                                      {brand}
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Machinery Groups + Items */}
                          <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                              <div>
                                <div className="text-sm font-black text-slate-900">Machinery Categories (Optional)</div>
                                <div className="text-[12px] text-slate-600 mt-1">
                                  Select groups & items you provide — better matching with farmer needs.
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <span className="text-[11px] font-semibold px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
                                  Groups: {form.machineryGroups.length}
                                </span>
                                <span className="text-[11px] font-semibold px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-700">
                                  Items: {form.machineryItems.length}
                                </span>
                              </div>
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2">
                              {MACH_GROUPS.map((g) => {
                                const active = form.machineryGroups.includes(g.id);
                                return (
                                  <button
                                    type="button"
                                    key={g.id}
                                    onClick={() => toggleMachGroup(g.id)}
                                    className={cx(
                                      "px-3 py-2 rounded-full border text-[12px] font-semibold transition-all duration-200",
                                      "focus:outline-none focus:ring-2 focus:ring-slate-900",
                                      active
                                        ? "border-emerald-300 bg-emerald-50 text-emerald-900 shadow"
                                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:shadow"
                                    )}
                                  >
                                    {active ? "✓ " : ""}{g.label}
                                  </button>
                                );
                              })}
                            </div>

                            {form.machineryGroups.length > 0 && (
                              <div className="mt-4 space-y-3">
                                {form.machineryGroups.map((gid) => {
                                  const grp = MACHINERY_MASTER[gid];
                                  if (!grp) return null;

                                  return (
                                    <div key={gid} className="rounded-3xl border border-slate-200 bg-slate-50 p-3">
                                      <div className="flex items-center justify-between gap-2">
                                        <div className="text-[12px] font-extrabold text-slate-900">{grp.label}</div>
                                        <span className="text-[11px] px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-700">
                                          {grp.items.length} items
                                        </span>
                                      </div>

                                      <div className="mt-2 flex flex-wrap gap-2">
                                        {grp.items.map((it) => {
                                          const active = form.machineryItems.some(
                                            (x) => x.groupId === gid && x.itemId === it.id
                                          );
                                          return (
                                            <button
                                              type="button"
                                              key={it.id}
                                              onClick={() => toggleMachItem(gid, it)}
                                              className={cx(
                                                "px-3 py-2 rounded-full border text-[12px] font-semibold transition",
                                                active
                                                  ? "border-slate-800 bg-white text-slate-900 shadow"
                                                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                                              )}
                                            >
                                              {active ? "✓ " : ""}{it.label}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Summary */}
                      <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                          Registration Summary
                        </div>

                        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-[12px]">
                          <MiniItem label="Company" value={form.companyName || "Not Provided"} />
                          <MiniItem
                            label="Taluka(s)"
                            value={form.serviceTalukas.length ? `${form.serviceTalukas.length} Selected` : "Not Selected"}
                          />
                          <MiniItem
                            label="Categories"
                            value={form.categories.length ? `${form.categories.length} Selected` : "None Selected"}
                          />
                        </div>

                        {form.serviceTalukas.length ? (
                          <div className="mt-3 text-[12px] text-slate-700">
                            <b>Selected Talukas:</b> {form.serviceTalukas.join(", ")}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>

                {/* FOOTER ACTIONS */}
                <div className="sticky bottom-0 border-t border-slate-200 bg-white/95 backdrop-blur px-5 md:px-6 py-4">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="text-[12px] text-slate-600">
                      Step {step} of 3 • Submission requires admin approval for activation.
                    </div>

                    <div className="flex gap-2 justify-end">
                      <button
                        type="button"
                        onClick={handleBack}
                        disabled={step === 1 || loading}
                        className={cx(
                          "px-4 py-2.5 rounded-2xl text-[12px] font-semibold border transition",
                          step === 1 || loading
                            ? "border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed"
                            : "border-slate-300 text-slate-700 bg-white hover:bg-slate-50"
                        )}
                      >
                        ← Back
                      </button>

                      {step < 3 ? (
                        <button
                          type="button"
                          onClick={handleNext}
                          disabled={loading}
                          className={cx(
                            "px-5 py-2.5 rounded-2xl text-[12px] font-semibold transition",
                            "bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed"
                          )}
                        >
                          Next →
                        </button>
                      ) : (
                        <button
                          type="submit"
                          disabled={loading}
                          className={cx(
                            "px-6 py-2.5 rounded-2xl text-[12px] font-semibold transition",
                            "bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                          )}
                        >
                          {loading ? (
                            <span className="inline-flex items-center gap-2">
                              <Spinner />
                              Processing...
                            </span>
                          ) : (
                            "Submit Registration"
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>

              <div className="mt-5 text-center text-sm text-slate-700">
                Already registered?{" "}
                <a
                  href="/vendor/login"
                  className="font-semibold text-slate-900 underline underline-offset-4 hover:text-emerald-700"
                >
                  Login here
                </a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   UI ATOMS
========================= */

function Card({ className = "", children }) {
  return (
    <div className={cx("rounded-3xl border border-slate-200 bg-white p-4 shadow-sm", className)}>
      {children}
    </div>
  );
}

function TopBadge({ label, value, tone = "slate" }) {
  const t =
    tone === "emerald"
      ? "bg-emerald-500/15 border-emerald-200/30 text-emerald-100"
      : tone === "amber"
      ? "bg-amber-500/15 border-amber-200/30 text-amber-100"
      : "bg-white/10 border-white/15 text-white/90";

  return (
    <span className={cx("text-[10px] px-3 py-1 rounded-full border", t)}>
      <span className="opacity-80">{label}:</span> <span className="font-semibold">{value}</span>
    </span>
  );
}

function TinyStat({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
      <div className="text-[11px] font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-[12px] font-extrabold text-slate-900">{value}</div>
    </div>
  );
}

function GuideRow({ active, n, title, desc }) {
  return (
    <div
      className={cx(
        "rounded-2xl border p-3 transition",
        active ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-white"
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cx(
            "h-8 w-8 rounded-2xl grid place-items-center text-[12px] font-black border",
            active ? "bg-white border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-700"
          )}
        >
          {n}
        </div>
        <div>
          <div className="text-[12px] font-extrabold text-slate-900">{title}</div>
          <div className="text-[12px] text-slate-600 mt-0.5">{desc}</div>
        </div>
      </div>
    </div>
  );
}

function StepChip({ active, label }) {
  return (
    <span
      className={cx(
        "text-[11px] px-3 py-1.5 rounded-full border font-semibold transition",
        active
          ? "bg-emerald-50 text-emerald-800 border-emerald-200"
          : "bg-white text-slate-600 border-slate-200"
      )}
    >
      {label}
    </span>
  );
}

function StepperLine({ step }) {
  const p = step === 1 ? 10 : step === 2 ? 50 : 100;
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center justify-between text-[11px] text-slate-600 font-semibold">
        <span className={step >= 1 ? "text-slate-900" : ""}>Company</span>
        <span className={step >= 2 ? "text-slate-900" : ""}>Location</span>
        <span className={step >= 3 ? "text-slate-900" : ""}>Categories</span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-white border border-slate-200 overflow-hidden">
        <div className="h-full bg-slate-900 transition-all duration-300" style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-2xl bg-white border border-slate-200 grid place-items-center text-slate-900">
          {icon}
        </div>
        <div>
          <div className="text-sm font-black text-slate-900">{title}</div>
          {subtitle && <div className="text-[12px] text-slate-600 mt-1">{subtitle}</div>}
        </div>
      </div>
    </div>
  );
}

function HintRow({ text }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-2xl bg-slate-900 text-white grid place-items-center shadow">
          <InfoIcon />
        </div>
        <div className="text-[12px] text-slate-700 leading-relaxed">{text}</div>
      </div>
    </div>
  );
}

function MiniItem({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
      <div className="text-[11px] font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-[12px] font-extrabold text-slate-900">{value}</div>
    </div>
  );
}

function Label({ children }) {
  return <label className="block text-[11px] font-semibold text-slate-700 mb-1">{children}</label>;
}

function selectClass(extra = "") {
  return cx(
    "w-full border border-slate-300 rounded-2xl px-3 py-3 text-sm bg-white",
    "focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900",
    "disabled:text-slate-400 disabled:cursor-not-allowed",
    extra
  );
}

function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  textarea = false,
  rows = 3,
  maxLength,
  inputMode,
}) {
  const commonClasses = cx(
    "w-full border border-slate-300 rounded-2xl px-3 py-3 text-sm bg-white",
    "focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900",
    "placeholder:text-slate-400"
  );

  return (
    <div>
      <Label>{label}</Label>
      {textarea ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          rows={rows}
          className={commonClasses}
          maxLength={maxLength}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          className={commonClasses}
          maxLength={maxLength}
          inputMode={inputMode}
        />
      )}
    </div>
  );
}

function Spinner() {
  return (
    <span className="inline-block h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
  );
}

/* ====== tiny inline icons (no library needed) ====== */
function ShieldIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2l8 4v6c0 5-3.4 9.8-8 10-4.6-.2-8-5-8-10V6l8-4z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function AlertIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 9v4m0 4h.01M10.3 3.6l-8.4 14.5A2 2 0 003.6 21h16.8a2 2 0 001.7-2.9L13.7 3.6a2 2 0 00-3.4 0z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
function InfoIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 22a10 10 0 110-20 10 10 0 010 20z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path d="M12 10v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 7h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
function OfficeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M3 21V7a2 2 0 012-2h6v16M13 9h6a2 2 0 012 2v10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <path d="M7 9h2M7 13h2M7 17h2M15 13h2M15 17h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 22s7-4.4 7-11a7 7 0 10-14 0c0 6.6 7 11 7 11z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path d="M12 11a2 2 0 100-4 2 2 0 000 4z" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}
function GridIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M4 4h7v7H4V4zM13 4h7v7h-7V4zM4 13h7v7H4v-7zM13 13h7v7h-7v-7z" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}
